import { Directive, HostListener, inject, ElementRef, input } from '@angular/core';
import { GoogleAnalyticsService } from 'src/app/core/core';

@Directive({
    selector: '[gtmClick]',
    exportAs: 'gtmClick',
    standalone: true,
})
export class GtmClickDirective {
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private elementRef = inject(ElementRef);

  readonly gtmName = input<string>(undefined, { alias: "gtmClick" });
  readonly gtmEventCategory = input<string>('click');
  readonly gtmClaimType = input<string>(undefined);
  readonly gtmLinkURL = input<string>(undefined);
  readonly gtmLinkClasses = input<string>(undefined);

  @HostListener('click') click() {
    // Use gtmLinkClasses to send different information or leave empty to get the css classes
    const classes = this.gtmLinkClasses() || this.getElementClasses(this.elementRef.nativeElement);

    // Resolve the CTA label: explicit gtmName wins; otherwise fall back to the
    // element's rendered text so link_text is never sent blank.
    const cleanGtmName = this.resolveGtmName();

    this.googleAnalyticsService.emitClickEvent(this.gtmEventCategory(), cleanGtmName, this.gtmLinkURL(), classes, this.gtmClaimType());
  }

  /**
   * Determines the CTA label to report as link_text.
   * Prefers the explicitly-provided gtmName; when it is missing or blank
   * (common when the label is rendered from Sitefinity via <adp-label>),
   * dynamically extracts the CTA's rendered text, then aria-label/title for
   * icon-only controls — so we never emit a blank link_text.
   */
  resolveGtmName(): string {
    const explicit = this.removeHtmlTags(this.gtmName()).trim();
    if (explicit) {
      return explicit;
    }

    return this.extractElementText(this.elementRef.nativeElement);
  }

  extractElementText(element: HTMLElement): string {
    if (!element) {
      return '';
    }

    const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) {
      return text;
    }

    // Icon-only CTAs carry no text — fall back to their accessible name.
    const ariaLabel = (element.getAttribute('aria-label') || element.getAttribute('title') || '').trim();
    return ariaLabel;
  }

  getElementClasses(element: HTMLElement): string {
    if (!element) {
      return '';
    }

    return element.className || '';
  }

  removeHtmlTags(input: string): string {
    if (!input) {
      return '';
    }
    // Replace all HTML tags with empty string
    return input.replace(/<\/?[^>]+(>|$)/g, '');
  }
}
